import React, { createContext, useCallback, useEffect, useState } from 'react';
import { propertyValuationApiRequests } from 'utils/api-requests/propertyValuation';
import { v2filtersApiRequests } from 'utils/api-requests/v2filters';
import { stringToNumber } from 'utils/formatters/stringToNumber';
import {
    PredictionHistoryDetailsDto,
    PredictionStatusDtoStatusEnum,
    PredictPriceDataDto,
    PredictPriceDataFullDto,
    PriceRangeResponseDto,
    PriceSearchValue
} from 'utils/types/AiPredictorModels';
import { ObjectAndSingleValueChangeType } from 'utils/types/InputTypes';
import type { SavedLocationsType } from 'utils/types/V2Locations';
import {
    filterValuationInitialState,
    initialValuationState,
    SelectedPriceRangeType,
    ValuationFiltersStateType,
    ValuationStateType,
    ValuationStep
} from 'utils/types/Valuation';

export interface ValuationContextType {
    valuationFiltersState: ValuationFiltersStateType;
    valuationState: ValuationStateType;
    handleChange: (name: string, value: ObjectAndSingleValueChangeType) => void
    clearFilters: () => void;
    clearResult: () => void;
    setLoadingPage: (load: boolean) => void;
    loaderText: string;
    currentStatus: PredictionStatusDtoStatusEnum | null;
    fetchPriceRange: () => Promise<void>;
    goToStep: (step: ValuationStep) => void;
    setSelectedPriceRange: (priceRange: SelectedPriceRangeType) => void;
    submitPrediction: () => Promise<void>;
    loadSavedReport: (uuid: string) => Promise<void>;
    deleteSavedReport: (uuid: string) => Promise<void>;
    isOpenResourcePopUp: boolean;
    closeResourcePopUp: () => void;
}

const values: ValuationContextType = {
    valuationFiltersState: {} as ValuationFiltersStateType,
    valuationState: {} as ValuationStateType,
    handleChange: () => {},
    clearFilters: () => {},
    clearResult: () => {},
    setLoadingPage: () => {},
    loaderText: 'Przygotowanie wyceny może potrwać do 2 min',
    currentStatus: null,
    fetchPriceRange: async () => {},
    goToStep: () => {},
    setSelectedPriceRange: () => {},
    submitPrediction: async () => {},
    loadSavedReport: async () => {},
    deleteSavedReport: async () => {},
    isOpenResourcePopUp: false,
    closeResourcePopUp: () => {}
};

export const PropertyValuationContext = createContext(values);

export const PropertyValuationProvider = ({
    children
}: {
    children: React.ReactNode;
}) => {
    const [valuationFiltersState, setValuationFiltersState] = useState<ValuationFiltersStateType>(filterValuationInitialState);
    const [valuationState, setValuationState] = useState<ValuationStateType>(initialValuationState);
    const [loaderText, setLoaderText] = useState('Pobieranie przedziałów cenowych...');
    const [currentStatus, setCurrentStatus] = useState<PredictionStatusDtoStatusEnum | null>(null);
    const [isOpenResourcePopUp, setIsOpenResourcePopUp] = useState(false);

    const closeResourcePopUp = () => setIsOpenResourcePopUp(false);

    const statusToLoaderText: Record<PredictionStatusDtoStatusEnum, string> = {
        [PredictionStatusDtoStatusEnum.STARTED]: 'Rozpoczynanie wyceny...',
        [PredictionStatusDtoStatusEnum.FETCHING_DATA]: 'Pobieranie danych...',
        [PredictionStatusDtoStatusEnum.DISPATCHING_TO_VALUATION]: 'Przygotowywanie wyceny...',
        [PredictionStatusDtoStatusEnum.CALCULATING_VALUATION]: 'Obliczanie wartości nieruchomości...',
        [PredictionStatusDtoStatusEnum.GENERATING_AI_REPORT]: 'Generowanie raportu AI...',
        [PredictionStatusDtoStatusEnum.COMPLETED]: 'Wycena zakończona',
        [PredictionStatusDtoStatusEnum.FAILED]: 'Błąd wyceny'
    };

    const handleStatusUpdate = (status: PredictionStatusDtoStatusEnum) => {
        const text = statusToLoaderText[status] || 'Przetwarzanie...';
        setLoaderText(text);
        setCurrentStatus(status);
    };

    const setLoadingPage = (load: boolean) => {
        setValuationState((prev) => ({
            ...prev,
            isLoading: load
        }));
    };

    const handleChange = useCallback(
        (fieldName: string, value: ObjectAndSingleValueChangeType) => {
            setValuationFiltersState((filters) => ({
                ...filters,
                [fieldName]: value
            }));
            setValuationState((prev) => ({
                ...prev,
                priceRangeResponse: undefined,
                selectedPriceRange: undefined,
                valuationResultV2: undefined,
                valuationAnalysis: undefined,
                similarFlat: [],
                similarFlatsFilter: undefined,
                selectedReportUuid: null
            }));
        },
        []
    );

    const clearFilters = () => {
        setValuationFiltersState((prev) => ({
            ...filterValuationInitialState,
            locations: prev.locations
        }));
        setValuationState((prev) => ({
            ...initialValuationState,
            savedReports: prev.savedReports
        }));
    };

    const clearResult = () => {
        setValuationState(initialValuationState);
    };

    const goToStep = (step: ValuationStep) => {
        setValuationState((prev) => ({
            ...prev,
            currentStep: step
        }));
    };

    const setSelectedPriceRange = (priceRange: SelectedPriceRangeType) => {
        setValuationState((prev) => ({
            ...prev,
            selectedPriceRange: priceRange
        }));
    };

    const getVoivodeships = async () => {
        const locations = await v2filtersApiRequests.getVoivodeships();
        setValuationFiltersState((prev: ValuationFiltersStateType) => ({
            ...prev,
            locations
        }));
    };

    const fetchPriceRange = async () => {
        setLoadingPage(true);
        setLoaderText('Pobieranie przedziałów cenowych...');

        const id = Object.values(valuationFiltersState.savedLocations)[0]?.id;

        const requestData: PredictPriceDataDto = {
            area: stringToNumber(valuationFiltersState.area) || 0,
            numberOfRooms: valuationFiltersState.numberOfRooms || 0,
            builtYear: stringToNumber(valuationFiltersState.builtYear) || 0,
            buildingType: valuationFiltersState.buildingType,
            buildingCondition: valuationFiltersState.buildingCondition,
            buildingOverallCondition: valuationFiltersState.buildingOverallCondition,
            floor: valuationFiltersState.floor || 0,
            floors: valuationFiltersState.floors || 0,
            typeOfMarket: valuationFiltersState.typeOfMarket,
            description: valuationFiltersState.description || '',
            buildingNumber: valuationFiltersState.buildingNumber || '',
            id
        };

        const priceRangeResponse = await propertyValuationApiRequests.getPriceRange(requestData);

        if (priceRangeResponse) {
            const initialSelectedPriceRange: SelectedPriceRangeType = {
                minPricePerSquareMeter: priceRangeResponse.minPricePerM2 || 0,
                maxPricePerSquareMeter: priceRangeResponse.maxPricePerM2 || 0,
                minPrice: priceRangeResponse.minPrice || 0,
                maxPrice: priceRangeResponse.maxPrice || 0,
                priceSearchValue: PriceSearchValue.CenaZaMetr
            };

            setValuationState((prev) => ({
                ...prev,
                priceRangeResponse,
                selectedPriceRange: initialSelectedPriceRange,
                isLoading: false
            }));
        } else {
            setLoadingPage(false);
        }
    };

    const submitPrediction = async () => {
        if (!valuationState.selectedPriceRange) {
            return;
        }

        setLoadingPage(true);
        setLoaderText('Tworzenie wyceny...');
        setCurrentStatus(PredictionStatusDtoStatusEnum.STARTED);

        const id = Object.values(valuationFiltersState.savedLocations)[0]?.id;
        const { selectedPriceRange } = valuationState;

        const requestData: PredictPriceDataFullDto = {
            area: stringToNumber(valuationFiltersState.area) || 0,
            numberOfRooms: valuationFiltersState.numberOfRooms || 0,
            builtYear: stringToNumber(valuationFiltersState.builtYear) || 0,
            buildingType: valuationFiltersState.buildingType,
            buildingCondition: valuationFiltersState.buildingCondition,
            buildingOverallCondition: valuationFiltersState.buildingOverallCondition,
            floor: valuationFiltersState.floor || 0,
            floors: valuationFiltersState.floors || 0,
            typeOfMarket: valuationFiltersState.typeOfMarket,
            description: valuationFiltersState.description || '',
            buildingNumber: valuationFiltersState.buildingNumber || '',
            id,
            minPricePerSquareMeter: selectedPriceRange.priceSearchValue === PriceSearchValue.CenaZaMetr
                ? selectedPriceRange.minPricePerSquareMeter
                : undefined,
            maxPricePerSquareMeter: selectedPriceRange.priceSearchValue === PriceSearchValue.CenaZaMetr
                ? selectedPriceRange.maxPricePerSquareMeter
                : undefined,
            minPrice: selectedPriceRange.priceSearchValue === PriceSearchValue.CenaZOgloszenia
                ? selectedPriceRange.minPrice
                : undefined,
            maxPrice: selectedPriceRange.priceSearchValue === PriceSearchValue.CenaZOgloszenia
                ? selectedPriceRange.maxPrice
                : undefined,
            priceSearchValue: selectedPriceRange.priceSearchValue,
            additionalInfo: valuationFiltersState.additionalInfo
        };

        const uuidData = await propertyValuationApiRequests.createPrediction(requestData);

        if (uuidData && 'errorStatus' in uuidData && uuidData.errorStatus === 423) {
            setLoadingPage(false);
            setCurrentStatus(null);
            setIsOpenResourcePopUp(true);

            return;
        }

        if (uuidData && 'uuid' in uuidData && uuidData.uuid) {
            const result = await propertyValuationApiRequests.getPredictionValuation(
                uuidData.uuid,
                handleStatusUpdate
            );

            if (result.success && result.data) {
                setValuationState((prev) => ({
                    ...prev,
                    valuationResultV2: result.data ?? undefined,
                    isLoading: false
                }));
                setCurrentStatus(null);
            } else {
                setLoadingPage(false);
                setCurrentStatus(null);
            }
        } else {
            setLoadingPage(false);
            setCurrentStatus(null);
        }
    };

    const fetchSavedReports = async () => {
        const reports = await propertyValuationApiRequests.getUserPredictions();

        setValuationState((prev) => ({
            ...prev,
            savedReports: reports
        }));
    };

    const handleFillFilterFields = (details: PredictionHistoryDetailsDto, locationLabel?: string) => {
        setValuationFiltersState((filters) => {
            const savedLocations: SavedLocationsType = {};

            if (details.locationId) {
                savedLocations[details.locationId] = {
                    id: details.locationId,
                    label: locationLabel || '',
                    children: []
                };
            }

            return {
                ...filters,
                savedLocations,
                buildingNumber: details.buildingNumber || '',
                area: details.area ? String(details.area) : '',
                builtYear: details.builtYear ? String(details.builtYear) : '',
                numberOfRooms: details.numberOfRooms || 0,
                buildingType: details.buildingType || filters.buildingType,
                buildingCondition: details.buildingCondition || filters.buildingCondition,
                buildingOverallCondition: (details.buildingOverallCondition || filters.buildingOverallCondition) as ValuationFiltersStateType['buildingOverallCondition'],
                floor: details.floor || 0,
                floors: details.floors || 0,
                typeOfMarket: details.typeOfMarket || filters.typeOfMarket,
                description: details.description || '',
                additionalInfo: details.additionalInfo || []
            };
        });
    };

    const deleteSavedReport = async (uuid: string) => {
        const success = await propertyValuationApiRequests.deletePrediction(uuid);

        if (success) {
            setValuationState((prev) => ({
                ...prev,
                savedReports: prev.savedReports.filter((r) => r.uuid !== uuid),
                valuationResultV2: prev.selectedReportUuid === uuid ? undefined : prev.valuationResultV2,
                selectedReportUuid: prev.selectedReportUuid === uuid ? null : prev.selectedReportUuid
            }));
        }
    };

    const loadSavedReport = async (uuid: string) => {
        setLoadingPage(true);
        setLoaderText('Ładowanie zapisanego raportu...');

        setValuationState((prev) => ({
            ...prev,
            selectedReportUuid: uuid
        }));

        const [resultV2, details] = await Promise.all([
            propertyValuationApiRequests.getPredictionResultV2(uuid),
            propertyValuationApiRequests.getPredictionDetails(uuid)
        ]);

        if (details) {
            const reportItem = valuationState.savedReports.find((r) => r.uuid === uuid);
            handleFillFilterFields(details, reportItem?.location);
        }

        const priceRangeFromDetails: { priceRangeResponse?: PriceRangeResponseDto; selectedPriceRange?: SelectedPriceRangeType } =
            details != null && (details.minPricePerSquareMeter != null || details.minPrice != null)
                ? {
                    priceRangeResponse: {
                        minPricePerM2: details.minPricePerSquareMeter,
                        maxPricePerM2: details.maxPricePerSquareMeter,
                        minPrice: details.minPrice,
                        maxPrice: details.maxPrice,
                        sameCondition: true
                    },
                    selectedPriceRange: {
                        minPricePerSquareMeter: details.minPricePerSquareMeter || 0,
                        maxPricePerSquareMeter: details.maxPricePerSquareMeter || 0,
                        minPrice: details.minPrice || 0,
                        maxPrice: details.maxPrice || 0,
                        priceSearchValue: details.priceSearchValue || PriceSearchValue.CenaZaMetr
                    }
                }
                : {};

        if (resultV2) {
            setValuationState((prev) => ({
                ...prev,
                valuationResultV2: resultV2,
                isLoading: false,
                selectedReportUuid: uuid,
                ...priceRangeFromDetails
            }));
        } else {
            setLoadingPage(false);
        }
    };

    useEffect(() => {
        fetchSavedReports();
    }, []);

    useEffect(() => {
        if (valuationFiltersState.locations.length === 0) {
            getVoivodeships();
        }
    }, [valuationFiltersState.locations]);

    const providerValues = {
        valuationState,
        valuationFiltersState,
        setLoadingPage,
        handleChange,
        clearFilters,
        clearResult,
        loaderText,
        currentStatus,
        fetchPriceRange,
        goToStep,
        setSelectedPriceRange,
        submitPrediction,
        loadSavedReport,
        deleteSavedReport,
        isOpenResourcePopUp,
        closeResourcePopUp
    };

    return (
        <PropertyValuationContext.Provider value={providerValues}>
            {children}
        </PropertyValuationContext.Provider>
    );
};

export default PropertyValuationContext;
